import { motion } from "framer-motion";
import CustomLists from "@/components/elements/CustomLists/CustomLists";
import ListWithDescription from "@/components/elements/CustomLists/ListWithDescription";
import ImageWithContentLeft from "@/components/elements/ImageWithContent/ImageWithContentLeft";
import Paragraph from "@/components/elements/Paragraph/Paragraph";
import TitleBar from "@/components/elements/TitleBar/TitleBar";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import c1 from "@/assets/images/services/crystalline-waterproofing.jpg"
import c2 from "@/assets/images/services/Crystalline.png"

const fadeInUp = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const CrystallineWaterproofing = () => {
  return (
    <div className="pb-0 px-4  2xl:px-40 xl:px-40 md:px-[3rem] my-[5rem]">
      <TitleBar title="Crystalline Waterproofing" />

      <motion.div {...fadeInUp}>
        <ImageWithContentLeft
          imageUrl={c1}
          title="Description"
          content="Crystalline waterproofing is a state-of-the-art system designed to provide deep, permanent protection for concrete structures. Unlike conventional surface membranes or coatings, crystalline technology works internally — penetrating deep into the concrete to seal pores, micro-cracks, and capillaries. The result is a self-healing, integral barrier that provides lifelong waterproofing and enhanced structural durability.
                    This technology is highly effective in both new constructions and remedial works, making it a preferred solution for high-risk waterproofing zones across commercial, industrial, and infrastructure projects."
        />
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="How it Works">
            <Paragraph content="Crystalline waterproofing involves the application of specially formulated chemicals that react with moisture and free lime in the concrete. This reaction initiates the formation of insoluble crystalline structures that:" />
            <CustomLists
              items={[ 
                ["Grow and expand to block water pathways.", 
                 "Fill and seal hairline cracks up to 0.4 mm.", 
                 "Remain dormant and reactivate when moisture re-enters the structure"]
              ]}
            />
            <Paragraph content="Because the crystals form deep within the concrete matrix, the waterproofing effect is permanent and cannot be damaged by surface wear or environmental exposure." />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Advantages of Crystalline Waterproofing">
          <ListWithDescription
            items={[
              {
                title: "Permanent, Integral Protection",
                content: ["Becomes part of the concrete itself — no delamination, punctures, or deterioration over time."]
              },
              {
                title: "Self-Healing Capability",
                content: ["Actively seals new hairline cracks when moisture is present, ensuring ongoing protection."]
              },
              {
                title: "Withstands Hydrostatic Pressure",
                content: ["Effectively resists both positive and negative water pressure, ideal for underground structures."]
              },
              {
                title: "Low Maintenance & Long Service Life",
                content: ["Requires no reapplication or special maintenance, significantly reducing lifecycle costs."]
              }
            ]}
          />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Applications of Crystalline Waterproofing">
          <CustomLists
            items={[
              [
                'Basements and Foundations',
                'Water Retaining Structures (e.g., reservoirs, swimming pools, tanks)',
                'Sewage and Wastewater Treatment Plants',
              ],
              [
                'Podiums and Roof Slabs',
                'Tunnels, Lift Pits, and Underground Parking',
                'Retaining Walls and Sea Walls',
              ]
            ]}
          />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Methods of Application">
          <img
            src={c2}
            alt="img"
            className="w-[clamp(300px,50vw,900px)] h-[clamp(200px,30vw,500px)] rounded-lg object-cover mx-auto my-4"
          />
          <ListWithDescription
            items={[
              {
                title: "Slurry Coating:",
                content: ["Applied to concrete surfaces as a brush-on or spray-applied slurry. Ideal for waterproofing existing structures."]
              },
              {
                title: "Dry Shake Method:",
                content: ["Broadcast directly onto fresh concrete and trowelled in during finishing. Commonly used on horizontal slabs."]
              },
              {
                title: "Admixture Integration:",
                content: ["Mixed directly into the concrete during batching, providing integral waterproofing from the outset. Ideal for new construction projects."]
              }
            ]}
          />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Why Choose Us for Crystalline Waterproofing?">
          <Paragraph
            content={
              <>
                At <span className="span-text">Sheel Waterproofing</span>, we specialize in crystalline waterproofing solutions that provide long-lasting protection against water ingress in a variety of structures. Our team is committed to delivering precise, high-quality application using the most advanced crystalline technology, ensuring that your property remains secure for years to come. Here’s why you should choose us:
              </>
            }
          />
          <CustomLists
            listStyle="none"
            layout="stacked"
            items={[
              [
                'Proven track record of successful crystalline waterproofing projects',
                'Certified applicators with extensive experience in crystalline technology',
                'Durable, self-healing protection that can withstand hydrostatic pressure',
                'No need for regular reapplication or maintenance, saving you money in the long run',
              ]
            ]}
            bullets={[["🏆", "🔧", "💧", "💸"]]}
          />
          <Paragraph
            content={
              <>
                With <span className="span-text">Sheel Waterproofing</span>, you can trust that your waterproofing needs will be met with the utmost care and precision. Our crystalline waterproofing solutions are designed to provide lasting results that ensure your property's integrity, even in the most challenging conditions.
                <br /><br />
                <a href="/contact" className="text-custom-hov underline">Contact us today</a> for a free consultation or site inspection.
              </>
            }
          />
        </TitleWithContent>
      </motion.div>

    </div>
  );
};

export default CrystallineWaterproofing;
